import { avatarSrc } from "@bn-games/auth-client";
import {
  getLobbyConnection,
  sendInvite,
  type Friend,
  type OnlineGame,
  type SocialError,
} from "@bn-games/social-client";
import { t } from "@bn-games/i18n-core";
import { el } from "./dom";

// Same reasoning as InviteCard.ts: every user-supplied string below reaches
// the DOM through `el`'s `text` prop (textContent) or an `attrs` entry
// (setAttribute), never `html` — so nothing here needs escaping.

const AVATAR_SIZE_PX = "28";

export interface FriendPickerRowOptions {
  readonly friend: Friend;
  readonly game: OnlineGame;
  readonly roomCode: string;
}

function createAvatar(friend: Friend): HTMLElement {
  const src = avatarSrc(friend.avatarUrl);
  return el("img", {
    class: "friend-picker__avatar",
    attrs: {
      ...(src !== undefined ? { src } : {}),
      alt: "",
      width: AVATAR_SIZE_PX,
      height: AVATAR_SIZE_PX,
    },
  });
}

/**
 * `forbidden` gets generic wording rather than `error.message` — the server
 * deliberately never tells the sender *why* the recipient blocked it (see
 * `NOT_ACCEPTING_ERROR` in `workers/auth/src/handlers/invites.ts`), so naming
 * a specific privacy setting here would leak more than the API returns.
 */
function messageFor(error: SocialError): string {
  if (error.kind === "forbidden") {
    return t("social.friendPicker.notAccepting");
  }
  if (error.kind === "rate_limited") {
    return error.retryAfterSeconds !== undefined
      ? t("social.friendPicker.retryIn", { seconds: error.retryAfterSeconds })
      : error.message;
  }
  return error.message;
}

/**
 * One friend, rendered as a row with its own Invite button. Success/failure
 * are handled locally (mutating this row's own button/error text) rather than
 * through a parent re-render, so one row's outcome never disturbs another
 * row's in-flight request or "Invited" state.
 */
export function createFriendPickerRow(opts: FriendPickerRowOptions): HTMLElement {
  const { friend, game, roomCode } = opts;
  const name = friend.displayName;

  const errorNode = el("span", { class: "friend-picker__row-error" });

  const button = el(
    "button",
    {
      class: "friend-picker__invite-btn",
      text: t("social.friendPicker.invite"),
      attrs: { type: "button", "aria-label": t("social.friendPicker.inviteAria", { name }) },
    },
    [],
  );

  let sending = false;

  button.addEventListener("click", () => {
    if (sending || button.disabled) return;
    sending = true;
    button.disabled = true;
    errorNode.textContent = "";

    void sendInvite({ toUserId: friend.userId, game, roomCode }).then((result) => {
      sending = false;
      if (result.ok) {
        // Best-effort live push; with no lobby connection the recipient still
        // finds the invite on their next poll.
        getLobbyConnection()?.sendInvite(result.value.id, friend.userId);
        button.textContent = t("social.friendPicker.invited");
        button.setAttribute("aria-label", t("social.friendPicker.invitedAria", { name }));
        button.disabled = true;
        return;
      }
      button.disabled = false;
      errorNode.textContent = messageFor(result.error);
    });
  });

  const info = el("div", { class: "friend-picker__info" }, [
    el("span", { class: "friend-picker__name", text: name }),
    friend.handle !== null
      ? el("span", { class: "friend-picker__handle", text: `@${friend.handle}` })
      : null,
    errorNode,
  ]);

  return el("div", { class: "friend-picker__row" }, [createAvatar(friend), info, button]);
}
